import { apiFetch } from './client';

export type DeliveryScope = 'city' | 'district' | 'region' | 'country' | 'international';

export const DELIVERY_SCOPE_LABELS: Record<DeliveryScope, string> = {
  city: 'Local (ville)',
  district: 'Régional (district)',
  region: 'Régional (région)',
  country: 'National',
  international: 'International (monde entier)'
};

export interface DelivererUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber?: string;
  profilePictureUrl?: string | null;
}

export interface DelivererRow {
  id: string;
  userId: string;
  companyName?: string;
  vehicleType: string;
  rating?: number;
  isAvailable: boolean;
  currency: string;
  pricePerKm?: number;
  minDeliveryPrice?: number;
  deliveryScope?: DeliveryScope;
  baseCity?: string;
  baseRegion?: string;
  baseCountry?: string;
  maxDistance?: number;
  User?: DelivererUser;
}

export type DeliverersResponse = { success: true; data: DelivererRow[] };

export const fetchDeliverers = () => apiFetch<DeliverersResponse>('/api/v1/deliverers');

/** Recherche publique de livreurs proches d'une localisation. */
export interface NearbyDeliverersParams {
  lat: number;
  lng: number;
  radius?: number;
}

export const fetchDeliverersNearLocation = ({ lat, lng, radius }: NearbyDeliverersParams) =>
  apiFetch<DeliverersResponse>(
    `/api/v1/deliverers/location/${encodeURIComponent(lat)}/${encodeURIComponent(lng)}`,
    { query: radius ? { radius } : undefined, auth: false }
  );

/** Recherche par critères (vehicleType, baseCity, etc.) — sans auth. */
export interface SearchDeliverersParams {
  vehicleType?: string;
  baseCity?: string;
  deliveryScope?: DeliveryScope;
  minRating?: number;
  acceptsCashOnDelivery?: boolean;
  acceptsElectronicPayment?: boolean;
}

export const searchDeliverers = (params: SearchDeliverersParams) =>
  apiFetch<DeliverersResponse>('/api/v1/deliverers/search', { query: params as any, auth: false });

/** Récupère le profil livreur d'un user (utilisé par le livreur pour son propre profil). */
export const fetchDelivererByUserId = (userId: string) =>
  apiFetch<{ success: true; data: DelivererRow }>(`/api/v1/deliverers/user/${userId}`);

/** Met à jour le profil livreur (auth requis, propriétaire ou admin). */
export interface UpdateDelivererInput {
  vehicleType?: string;
  deliveryScope?: DeliveryScope;
  baseCity?: string;
  baseRegion?: string;
  baseCountry?: string;
  maxDistance?: number;
  companyName?: string;
  pricePerKm?: number;
  minDeliveryPrice?: number;
  currency?: string;
  isAvailable?: boolean;
}

export const updateDeliverer = (id: string, payload: UpdateDelivererInput) =>
  apiFetch<{ success: true; data: DelivererRow }>(`/api/v1/deliverers/${id}`, {
    method: 'PUT',
    body: payload
  });

/** Mise à jour rapide de la disponibilité (PATCH dédié). */
export const updateDelivererAvailability = (id: string, isAvailable: boolean) =>
  apiFetch<{ success: true; data: DelivererRow }>(`/api/v1/deliverers/${id}/availability`, {
    method: 'PATCH',
    body: { isAvailable }
  });

/** Création de profil livreur (un user existant devient livreur). */
export interface CreateDelivererInput {
  userId: string;
  vehicleType: string;
  deliveryScope?: DeliveryScope;
  baseCity?: string;
  baseCountry?: string;
  pricePerKm?: number;
  currency?: string;
}

export const createDeliverer = (input: CreateDelivererInput) =>
  apiFetch<{ success: true; data: DelivererRow }>('/api/v1/deliverers', {
    method: 'POST',
    body: input
  });
